/**
 * apiFetch — centralized fetch wrapper with global auth-error handling.
 *
 * 401 → logout + redirect to login  (with a hook for future "remember me" recovery)
 * 403 → redirect to /not-found      (caller can opt out for soft-error endpoints)
 *
 * Drop-in replacement for `fetch()`: same signature plus an optional third
 * argument for wrapper-specific options.
 *
 * Usage:
 *   import { apiFetch } from '../utils/apiFetch';
 *   const response = await apiFetch('/api/foo/', { method: 'POST', headers: getAuthHeaders(), body });
 *
 * To skip global handling (login routes, file-download helpers, etc.):
 *   const response = await apiFetch(url, init, { skipAuthHandling: true });
 */

import { logout } from "../../auth";

// ─── Re-entrancy guard ──────────────────────────────────────────────────────
// Multiple concurrent requests may hit 401 at the same time.
// Only the first one triggers logout + redirect; the rest just reject.
let isHandling401 = false;

// ─── Path allowlist — never run global auth handling for these prefixes ─────
const AUTH_SAFE_PREFIXES = [
  "/api/auth/",
  "/api/services/auth/refresh-token",
  "/token",
];

function isAuthSafePath(url) {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    return AUTH_SAFE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  } catch {
    return false;
  }
}

// ─── 401 recovery hook (placeholder for future "remember me") ───────────────
/**
 * Override this function to attempt session recovery before logout.
 * Return { recovered: true } to retry the original request with a new token.
 * Default: no recovery, always returns { recovered: false }.
 *
 * @param {Response} _response - The 401 response from the server.
 * @returns {Promise<{ recovered: boolean }>}
 */
export async function tryRecoverFrom401(_response) {
  // TODO: implement remember-me / refresh-token recovery here.
  //   Example:
  //     const result = await refreshSession();
  //     if (result.ok) return { recovered: true };
  return { recovered: false };
}

// ─── Main wrapper ───────────────────────────────────────────────────────────

/**
 * @param {RequestInfo} input    - URL string or Request object (same as fetch).
 * @param {RequestInit} [init]   - Standard fetch init options.
 * @param {object}      [meta]   - Wrapper-specific options.
 * @param {boolean}     [meta.skipAuthHandling=false] - Skip 401/403 global handling.
 * @returns {Promise<Response>}  - The raw Response (caller parses body as usual).
 */
export async function apiFetch(input, init, meta = {}) {
  const { skipAuthHandling = false } = meta;

  const response = await fetch(input, init);

  // Determine whether global handling applies for this request.
  const url = typeof input === "string" ? input : input?.url ?? "";
  const shouldHandle = !skipAuthHandling && !isAuthSafePath(url);

  if (!shouldHandle) return response;

  // ─── 401: Unauthenticated → logout + login ─────────────────────────────
  if (response.status === 401) {
    if (!isHandling401) {
      isHandling401 = true;
      try {
        const recovery = await tryRecoverFrom401(response);
        if (recovery.recovered) {
          isHandling401 = false;
          // Retry once with the (now-refreshed) token — caller must supply
          // updated headers on retry, so we re-run the same call.
          const retryHeaders = { ...Object.fromEntries(new Headers(init?.headers)) };
          const newToken = localStorage.getItem("token");
          if (newToken) retryHeaders["Authorization"] = `Bearer ${newToken}`;
          return fetch(input, { ...init, headers: retryHeaders });
        }

        // logout({ setExpired: true }) captures the current URL internally
        const { redirectPath } = logout({ setExpired: true });
        // window.location.assign(redirectPath);
      } finally {
        isHandling401 = false;
      }
    }

    // Whether we redirected or another call is already handling it,
    // reject so the caller doesn't try to parse a 401 body.
    const error = new Error("Session expired");
    error.status = 401;
    throw error;
  }

  // ─── 403: Forbidden → /not-found ───────────────────────────────────────
  if (response.status === 403) {
    window.location.assign("/not-found");

    const error = new Error("Access denied");
    error.status = 403;
    throw error;
  }

  return response;
}
